import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, resolveImg } from "@/lib/api";
import { Flame, ArrowRight } from "lucide-react";

/**
 * UP NEXT — the sidebar's headline widget. It shows a big lead thumbnail with
 * a bold overlay + two secondary picks stacked underneath. Designed to pull
 * the reader forward into another story before they bail from the article.
 *
 * Order of preference for the picks:
 *   1) "related" articles for this article (server-computed)
 *   2) latest news (filtered to exclude the current article)
 */
export default function UpNextWidget({ articleId }) {
  const [picks, setPicks] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const collected = [];
      try {
        const { data } = await api.get(`/news/${articleId}/related`, {
          params: { limit: 4 },
        });
        (data || []).forEach((a) => {
          if (a.id !== articleId) collected.push(a);
        });
      } catch { /* fallthrough */ }
      if (collected.length < 3) {
        try {
          const { data } = await api.get(`/news`, { params: { limit: 8 } });
          (data?.articles || []).forEach((a) => {
            if (
              a.id !== articleId &&
              !collected.find((p) => p.id === a.id)
            ) {
              collected.push(a);
            }
          });
        } catch { /* silent */ }
      }
      if (!cancelled) setPicks(collected.slice(0, 3));
    })();
    return () => { cancelled = true; };
  }, [articleId]);

  if (!picks.length) return null;
  const [lead, ...rest] = picks;

  return (
    <div
      className="titans-card p-0 overflow-hidden relative"
      data-testid="up-next-widget"
    >
      {/* Header strip */}
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <div className="flex items-center gap-2">
          <div className="h-1.5 w-1.5 bg-titans-red animate-pulse rounded-full" />
          <span className="font-cond uppercase tracking-[0.28em] text-[10px] text-titans-red">
            Up Next
          </span>
        </div>
        <Flame size={13} className="text-titans-red" />
      </div>

      {/* Lead card */}
      <Link
        to={`/news/${lead.slug || lead.id}`}
        state={{ article: lead }}
        data-testid="up-next-lead"
        className="group relative block aspect-[16/10] overflow-hidden"
      >
        <img
          src={resolveImg(lead.image) || "https://images.pexels.com/photos/264305/pexels-photo-264305.jpeg?auto=compress&cs=tinysrgb&h=400&w=640"}
          alt={lead.headline}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          loading="lazy"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
        <div className="absolute top-2 left-2">
          <span className="bg-titans-red text-white font-cond uppercase tracking-[0.18em] text-[9px] px-2 py-1">
            {lead.category || "Story"}
          </span>
        </div>
        <div className="absolute inset-x-0 bottom-0 p-3">
          <div className="font-display uppercase text-white text-base leading-tight tracking-wide line-clamp-3 group-hover:text-titans-red transition-colors">
            {lead.headline}
          </div>
          <div className="mt-2 inline-flex items-center gap-1 text-titans-blue font-cond uppercase tracking-[0.2em] text-[10px] group-hover:text-white transition-colors">
            Read now <ArrowRight size={11} />
          </div>
        </div>
      </Link>

      {/* Secondary picks */}
      {rest.length > 0 && (
        <ul className="divide-y divide-[rgba(15,23,42,0.08)] px-4 pt-1 pb-4">
          {rest.map((a, i) => (
            <li key={a.id}>
              <Link
                to={`/news/${a.slug || a.id}`}
                state={{ article: a }}
                data-testid={`up-next-pick-${i}`}
                className="flex gap-3 py-3 group items-center"
              >
                <div className="w-14 h-14 shrink-0 overflow-hidden bg-titans-void relative">
                  {a.image && (
                    <img
                      src={resolveImg(a.image)}
                      alt=""
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      loading="lazy"
                      decoding="async"
                    />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-cond uppercase tracking-[0.22em] text-[9px] text-titans-red mb-0.5">
                    {a.category || "Story"}
                  </div>
                  <div className="font-display uppercase text-[13px] leading-snug line-clamp-2 group-hover:text-titans-red transition-colors">
                    {a.headline}
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
